import { useRef, useState, useId, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';

import type { KeyboardEvent, ChangeEvent, MouseEvent } from 'react';

import styles from './MultiSelectBox.module.css';

import { Icon } from 'components/Icon';
import { Checkbox } from 'components/Checkbox';
import { usePositionedFloaters } from 'components/hooks/usePositionedFloaters';

export type MultiSelectData = {
  label: string;
  value: string | number;
  disabled?: boolean;
};

export interface MultiSelectBoxProps {
  /** Array of label, values pairs */
  selectData: MultiSelectData[];
  /** Retuns selected label, values */
  onChange: (values: MultiSelectData[]) => void;
  /** Array of label, value pairs that should be check by default */
  defaultSelectedItems?: MultiSelectData[];
  /** Suffix label in the selectbutton '[count] selected' */
  labelSuffix?: string;
  /** Disables the multiselect */
  disabled?: boolean;
  /** If true sets the option dropdown min-width to the width of the select button. */
  fullWidth?: boolean;
  /** Dark border. */
  darkBorder?: boolean;
  /** The max height of the options. */
  maxOptionHeight?: `${number}${'px' | 'rem'}`;
  /** `true` to align the right side of the options with the select box; otherwise, `false` to align on the left side. */
  optionPositionRight?: boolean;
}

export const MultiSelectBox = ({
  selectData,
  onChange,
  defaultSelectedItems,
  labelSuffix = 'geselecteerd',
  disabled = false,
  fullWidth = false,
  darkBorder = false,
  maxOptionHeight,
  optionPositionRight,
}: MultiSelectBoxProps) => {
  const multiselectboxId = useId();
  const [selectedItems, setSelectedItems] = useState<MultiSelectData[]>(defaultSelectedItems || []);
  const [selectedItemsCount, setSelectedItemsCount] = useState(defaultSelectedItems?.length || 0);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const optionListRef = useRef<HTMLUListElement>(null);

  const [positionElement, setPositionElement] = useState<HTMLElement | null>(null);
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const { style: positionStyle, updatePosition } = usePositionedFloaters(positionElement, referenceElement, {
    mouseEvent: 'click',
    position: 'bottomLeft',
    offset: 4,
    maxFixedHeight: maxOptionHeight,
    horizontalPosition: optionPositionRight ? 'right' : 'left',
    mobileBreakpoint: '28rem',
    fullWidth,
  });

  const onMouseEventOutside = useCallback(
    ({ target }: Event | MouseEvent) => {
      if (!target || !wrapperRef.current) return;

      if (!wrapperRef.current.contains(target as Node)) {
        setIsOpen(false);
      }
    },
    [setIsOpen]
  );

  const onTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          setIsOpen(true);
          break;
        case 'Escape':
          if (isOpen) {
            event.stopPropagation();
            setIsOpen(false);
          }
          break;
        default:
          break;
      }
    },
    [isOpen]
  );

  const onOptionKeyDown = useCallback(
    (event: KeyboardEvent<HTMLLIElement>) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex((prev) => {
            let nextIndex = prev;
            do {
              nextIndex = nextIndex < selectData.length - 1 ? nextIndex + 1 : 0;
            } while (selectData[nextIndex]?.disabled);
            return nextIndex;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex((prev) => {
            let nextIndex = prev;
            do {
              nextIndex = nextIndex <= 0 ? selectData.length - 1 : nextIndex - 1;
            } while (selectData[nextIndex]?.disabled);
            return nextIndex;
          });
          break;
        case 'Enter': {
          event.preventDefault();
          const selectedItem = selectData[activeIndex];
          if (!selectedItem || selectedItem.disabled) break;
          const isAlreadySelected = selectedItems.some((item) => item.value === selectedItem.value);
          const updatedSelectedItems = isAlreadySelected
            ? selectedItems.filter((item) => item.value !== selectedItem.value)
            : [...selectedItems, selectedItem];

          setSelectedItems(updatedSelectedItems);
          setSelectedItemsCount(updatedSelectedItems.length);
          onChange(updatedSelectedItems);
          break;
        }
        case 'Tab':
          event.preventDefault();
          setIsOpen(false);
          break;
        case 'Escape':
          event.stopPropagation();
          setIsOpen(false);
          break;
        default:
          break;
      }
    },
    [activeIndex, isOpen, selectData, selectedItems, onChange]
  );

  const onOptionClick = (event: MouseEvent<HTMLLIElement>, option: MultiSelectData) => {
    if (option.disabled) return;

    // Prevent toggle if click originated from the checkbox
    if ((event.target as HTMLElement).tagName === 'INPUT') return;

    const isAlreadySelected = selectedItems.some((item) => item.value === option.value);
    const updatedSelectedItems = isAlreadySelected
      ? selectedItems.filter((item) => item.value !== option.value)
      : [...selectedItems, option];

    setSelectedItems(updatedSelectedItems);
    setSelectedItemsCount(updatedSelectedItems.length);
    onChange(updatedSelectedItems);
    updatePosition();
  };

  const handleSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked, value } = event.target;
      let updatedSelectedItems = [...selectedItems];

      if (checked) {
        const selectedItem = selectData.find((item) => item.value === value);
        if (selectedItem) {
          updatedSelectedItems.push(selectedItem);
        }
      } else {
        updatedSelectedItems = updatedSelectedItems.filter((item) => item.value !== value);
      }

      setSelectedItems(updatedSelectedItems);
      setSelectedItemsCount(updatedSelectedItems.length);
      onChange(updatedSelectedItems);
      updatePosition();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectData, selectedItems, onChange]
  );

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const activeOption = optionListRef.current?.children[activeIndex] as HTMLElement;
    activeOption?.focus();
  }, [activeIndex]);

  useEffect(() => {
    if (!isOpen) return;

    document.addEventListener('click', onMouseEventOutside, true);
    document.addEventListener('scroll', onMouseEventOutside, true);

    return () => {
      document.removeEventListener('click', onMouseEventOutside, true);
      document.removeEventListener('scroll', onMouseEventOutside, true);
    };
  }, [onMouseEventOutside, isOpen]);

  return (
    <div className={styles['multi-select-box-wrapper']} ref={wrapperRef}>
      <button
        className={clsx(styles['multi-select-box-trigger'], {
          [styles['multi-select-box-trigger--darkBorder']]: darkBorder,
        })}
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        onKeyDown={onTriggerKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`${selectedItemsCount} ${labelSuffix}`}
        disabled={disabled}
        {...(isOpen && { 'aria-controls': multiselectboxId })}
        ref={setReferenceElement}
      >
        <span className={styles['multi-select-box-trigger-label']}>
          {selectedItemsCount} {labelSuffix}
        </span>
        <Icon
          className={clsx(styles['multi-select-box-trigger-icon'], {
            [styles['multi-select-box-trigger-icon--open']]: isOpen,
          })}
          name="CheveronDown"
          size={16}
        />
      </button>

      <div
        id={multiselectboxId}
        className={clsx(styles['multi-select-box-listbox--wrapper'])}
        style={{ ...positionStyle.style, visibility: isOpen ? 'visible' : 'hidden' }}
        role="listbox"
        ref={setPositionElement}
      >
        <ul className={styles['multi-select-box-listbox']} ref={optionListRef}>
          {selectData.map(({ label, value, disabled }, index) => (
            <li
              className={clsx(styles['multi-select-box-listbox-option'], {
                [styles['multi-select-box-listbox-option--active']]: index === activeIndex,
                [styles['multi-select-box-listbox-option--disabled']]: disabled,
              })}
              key={value}
              role="option"
              onKeyDown={onOptionKeyDown}
              onClick={(event) => onOptionClick(event, { label, value, disabled })}
              onMouseOver={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              tabIndex={0}
              aria-selected={selectedItems.some((item) => item.value === value)}
            >
              <label htmlFor={label} className={styles['multi-select-box-listbox-option-content']} tabIndex={-1}>
                <Checkbox
                  id={`${label}-${multiselectboxId}`}
                  onChange={handleSelect}
                  value={value}
                  tabIndex={-1}
                  disabled={disabled}
                  checked={selectedItems.some((item) => item.value === value)}
                />
                <span>{label}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
